import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--command-palette"
//
// Hotwire 1:1 replica of the React SearchModal behaviour. Uses the native
// <dialog> element for open/close/backdrop/focus-trap (no modal lib), and adds:
//   - ⌘/Ctrl+K global open shortcut (React SearchButton)
//   - debounced quick-search that swaps the results turbo-frame src
//   - roving-focus keyboard nav (Arrow/Enter) across the visible option links,
//     mirroring cmdk's loop selection
//   - recent-searches persistence, reading/writing the SAME localStorage key and
//     shape as the React zustand store ("search-modal-store") so history carries
//     over across the migration (CONEK-2341 UUID-key pruning included)
//
// Results / action items / recent searches are all plain <a role="option">
// elements tagged as targets; selection is a single "aria-selected" class hop.
export default class extends Controller {
  static targets = [
    "dialog",
    "input",
    "clear",
    "loading",
    "error",
    "actions",
    "recentSearches",
    "recentSearchesList",
    "trigger",
    "metaKey",
    "item",
    "actionItem",
    "scan",
  ]

  static values = {
    searchUrl: String,
    resultsUrl: String,
    triggerKey: { type: String, default: "k" },
    minLength: { type: Number, default: 3 },
    debounce: { type: Number, default: 250 },
    actionsListboxId: String,
    resultsListboxId: String,
    // Recent-searches scope. Empty (default) → derived from the URL's space
    // prefix (CRM). Platform-wide surfaces with no space prefix (docs) pass an
    // explicit sentinel (e.g. "docs") so their history stays isolated from CRM's.
    recentScope: { type: String, default: "" },
  }

  static STORAGE_KEY = "search-modal-store"
  static MAX_RECENT = 10

  // Window-event contract (spec §5.1): external surfaces (the React navbar
  // trigger on Inertia pages, the drawings-editor toolbar) open the dialog by
  // dispatching this on `window` — no DOM reference needed. Namespaced with a
  // colon to match the sidebar event convention; carries no payload. Kept in
  // sync with Ui::CommandPaletteComponent::OPEN_EVENT.
  static OPEN_EVENT = "command-palette:open"

  // CONEK-8571: dispatched (bubbling, from this controller's element) when the
  // Scan button is pressed, with `detail.scanUrl` — the host-passed scan_url.
  // The kit owns no camera: the host listens, opens its scanner and resolves
  // the code. Kept in sync with Ui::CommandPaletteComponent::SCAN_EVENT.
  static SCAN_EVENT = "command-palette:scan"

  connect() {
    this._selectedIndex = -1
    this._optionSeq = 0
    this._debounceTimer = null
    // True between _performSearch and the frame's next completion. Set only by
    // keystroke searches (NOT by §7.3 load-more link clicks), so on the next
    // frame load we can tell a fresh search (clear the selection) from a
    // load-more (preserve the user's selection). Also gates the error handlers:
    // a failed frame request (non-2xx) never fires turbo:frame-load, so without
    // this the spinner would hang — see H1.
    this._pendingFrame = false
    this._globalKeydown = this._onGlobalKeydown.bind(this)
    this._globalOpen = this._onGlobalOpen.bind(this)
    this._frameLoad = this._onFrameLoad.bind(this)
    this._fetchResponse = this._onFetchResponse.bind(this)
    this._fetchError = this._onFetchError.bind(this)
    document.addEventListener("keydown", this._globalKeydown)
    window.addEventListener(this.constructor.OPEN_EVENT, this._globalOpen)
    document.addEventListener(
      "turbo:before-fetch-response",
      this._fetchResponse,
    )
    document.addEventListener("turbo:fetch-request-error", this._fetchError)

    const frame = this._frame()
    if (frame) frame.addEventListener("turbo:frame-load", this._frameLoad)

    if (this.hasMetaKeyTarget) {
      this.metaKeyTarget.textContent = this._isMac() ? "⌘" : "Ctrl"
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._globalKeydown)
    window.removeEventListener(this.constructor.OPEN_EVENT, this._globalOpen)
    document.removeEventListener(
      "turbo:before-fetch-response",
      this._fetchResponse,
    )
    document.removeEventListener("turbo:fetch-request-error", this._fetchError)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    const frame = this._frame()
    if (frame) frame.removeEventListener("turbo:frame-load", this._frameLoad)
  }

  // ── Open / close ─────────────────────────────────────────────────────────

  open() {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
    this._renderRecentSearches()
    this._showActions(true)
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  // Scan button (rendered only when the host passed scan_url). Closes the
  // palette FIRST so the host's scanner is not opened underneath a top-layer
  // <dialog> (showModal makes everything outside it inert), then hands the
  // host the URL to resolve the scanned code against. The event is dispatched
  // on this.element — which stays connected after close — so it bubbles to a
  // host controller wrapping the palette, and on to document.
  scan(event) {
    event?.preventDefault?.()
    const scanUrl = event?.params?.scanUrl
    if (!scanUrl) return
    this.close()
    this.element.dispatchEvent(
      new CustomEvent(this.constructor.SCAN_EVENT, {
        bubbles: true,
        detail: { scanUrl },
      }),
    )
  }

  onClose() {
    // Reset state so the next open starts clean (parity with React handleClose).
    this.inputTarget.value = ""
    this._clearFrame()
    this._showActions(true)
    this._resetActiveDescendant()
    this.clearTarget.classList.add("hidden")
  }

  // Native <dialog> backdrop click: the click target is the dialog itself.
  backdropClose(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  // Mouse selection: persist to recent searches before the <a> navigates.
  // cmdk's onSelect fires for pointer AND keyboard; the keyboard path goes
  // through _selectOption, so we mirror it here for clicks (CONEK-2341 parity —
  // most users click rather than arrow+Enter). No preventDefault: the link
  // navigates normally, we just record it synchronously first.
  selectResult(event) {
    const option = event.target.closest("[data-recent-search-name]")
    if (!option) return
    const name = option.dataset.recentSearchName
    const href = option.dataset.recentSearchHref
    if (name && href) this._saveRecentSearch(name, href)
  }

  // §7.3 load-more click. The link is INSIDE the results <dialog>, where Turbo's
  // global click interceptor does not reliably catch it (observed: a click
  // full-page-navigated to the bare layout:false /search/quick fragment despite
  // data-turbo-frame being present). So instead of relying on Turbo reading
  // data-turbo-frame, we drive the frame directly: preventDefault and assign
  // frame.src — the exact mechanism the search input already uses. The frame
  // swap then re-renders with the deeper section, in-place, palette intact.
  // _pendingFrame stays unset so _onFrameLoad keeps the cursor position.
  loadMore(event) {
    const link = event.target.closest("a[data-turbo-frame]")
    if (!link) return
    const frame = this._frame()
    if (!frame || !link.href) return
    event.preventDefault()
    frame.src = link.href
  }

  clear() {
    this.inputTarget.value = ""
    this.clearTarget.classList.add("hidden")
    this._clearFrame()
    this._showActions(true)
    this.inputTarget.focus()
  }

  // ── Search ───────────────────────────────────────────────────────────────

  onInput() {
    const query = this.inputTarget.value.trim()
    this.clearTarget.classList.toggle("hidden", query.length === 0)

    if (this._debounceTimer) clearTimeout(this._debounceTimer)

    if (query.length < this.minLengthValue) {
      this._clearFrame()
      this._showActions(true)
      this._setLoading(false)
      return
    }

    // CONEK-7194: the React modal filtered the action items as you typed; the
    // first Hotwire port hid the WHOLE table on the first keystroke instead, so
    // a query naming a page ("cal" → Calendar) lost its shortcut with no
    // results yet to replace it. Filter the rows (per <tr>, so a group heading
    // and its cells fade together): matching shortcuts stay visible alongside
    // the live results, and the container hides only when NO row matches — the
    // old behavior, for a query that names no page. The debounced search runs
    // either way; its results render in the frame.
    const visibleRows = this._filterActions(query)
    if (visibleRows > 0) {
      // Recent searches belong to the idle state; typing hides them (React
      // parity). A previous zero-match keystroke may have hidden the container
      // and pointed aria-controls at the results — both come back with the
      // matches. The rows themselves keep the filter.
      if (this.hasRecentSearchesTarget)
        this.recentSearchesTarget.classList.add("hidden")
      this.actionsTarget.classList.remove("hidden")
      this._pointAriaControls(this.actionsListboxIdValue)
    } else {
      this._showActions(false)
    }
    this._setLoading(true)
    this._debounceTimer = setTimeout(
      () => this._performSearch(query),
      this.debounceValue,
    )
  }

  _performSearch(query) {
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", query)
    const frame = this._frame()
    // Mark this frame-load as a fresh keystroke search so _onFrameLoad clears the
    // keyboard cursor (as opposed to a §7.3 load-more click, which
    // must preserve it). Set before assigning src so any immediate failure is
    // still gated on it.
    this._pendingFrame = true
    if (frame) frame.src = url.toString()
  }

  _onFrameLoad() {
    this._setLoading(false)
    this._setError(false)
    const options = this._options()
    if (options.length === 0) {
      this._resetActiveDescendant()
      return
    }
    if (this._pendingFrame) {
      // Fresh keystroke search: do NOT auto-highlight the first result
      // (CONEK-7048). The first option used to be the safe "view more results"
      // link, so auto-highlighting row 0 was harmless — but §7.2 moved that
      // link below the sections, making row 0 the top RESULT row, and Enter
      // then opened the first customer outright. Now Enter with no arrowing
      // must land on the full results page so the user chooses manually; the
      // cursor only comes back when the user arrows (or hovers) onto a row, and
      // Enter with an explicit selection still activates it.
      this._pendingFrame = false
      this._resetActiveDescendant()
    } else {
      // §7.3 load-more (an anchor click navigates the frame): keep where the user
      // was, clamped to the grown option set, instead of snapping back to the top.
      // A user who never moved the cursor (-1) stays unselected, so Enter keeps
      // opening the full results page after a load-more too.
      if (this._selectedIndex < 0) {
        this._resetActiveDescendant()
        return
      }
      const index = Math.min(this._selectedIndex, options.length - 1)
      this._moveTo(index, options)
    }
  }

  _resetActiveDescendant() {
    this._selectedIndex = -1
    if (this.hasInputTarget)
      this.inputTarget.removeAttribute("aria-activedescendant")
  }

  // ── Keyboard nav (roving focus across visible option links) ───────────────

  onKeydown(event) {
    const options = this._options()
    switch (event.key) {
      case "ArrowDown":
        if (options.length === 0) break
        event.preventDefault()
        this._moveTo(
          (this._selectedIndex + 1 + options.length) % options.length,
          options,
        )
        break
      case "ArrowUp":
        if (options.length === 0) break
        event.preventDefault()
        this._moveTo(
          this._selectedIndex <= 0
            ? options.length - 1
            : this._selectedIndex - 1,
          options,
        )
        break
      // Home/End are intentionally NOT intercepted: in the APG combobox-with-list
      // pattern focus never leaves the input, so those keys must move the native
      // text cursor to the start/end of the query. Only the arrows move the
      // virtual listbox cursor.
      case "Enter": {
        const option = options[this._selectedIndex]
        if (option) {
          event.preventDefault()
          this._selectOption(option)
        } else {
          // No option highlighted — the CONEK-7048 default. Pressing Enter
          // without arrowing must NOT open the first result; it goes to the
          // full results page so the user picks manually. (The frame may also
          // still be loading, or the query too short to fetch.) When a cursor
          // IS set — by arrowing or hovering — Enter activates that option.
          const query = this.hasInputTarget ? this.inputTarget.value.trim() : ""
          if (query.length > 0 && this.hasResultsUrlValue) {
            event.preventDefault()
            window.location.href = this._resultsUrlFor(query)
          }
        }
        break
      }
      // Escape is handled natively by <dialog>.
    }
  }

  // Full results-page URL for a query — mirrors the server's view_more_href
  // (results_path(search:)), built client-side so Enter needn't wait for the frame.
  _resultsUrlFor(query) {
    const url = new URL(this.resultsUrlValue, window.location.origin)
    url.searchParams.set("search", query)
    return url.toString()
  }

  _moveTo(index, options) {
    if (options.length === 0) return
    this._selectedIndex = index
    this._highlight(options)
  }

  // APG listbox: focus stays in the input; the active option is a virtual
  // cursor announced via aria-activedescendant (not real DOM focus).
  _highlight(options) {
    let activeId = null
    options.forEach((option, i) => {
      const active = i === this._selectedIndex
      // Ensure every option has a stable id so aria-activedescendant can point at it.
      if (!option.id) option.id = `cp-opt-${this._optionSeq++}`
      option.setAttribute("aria-selected", active ? "true" : "false")
      if (active) {
        activeId = option.id
        option.scrollIntoView({ block: "nearest" })
      }
    })
    if (this.hasInputTarget) {
      if (activeId)
        this.inputTarget.setAttribute("aria-activedescendant", activeId)
      else this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  _selectOption(option) {
    // Persist to recent searches when the option carries a display name
    // (result rows do; action items intentionally don't — parity with React
    // onSelectAddToRecentSearches=false).
    const name = option.dataset.recentSearchName
    const href = option.dataset.recentSearchHref
    if (name && href) this._saveRecentSearch(name, href)
    // An option declaring data-turbo-frame (the §7.3 load-more link) must
    // navigate THAT frame: window.location.href would escape to a full-page
    // visit of the layout:false fragment. The click path is scoped by Turbo's
    // interceptor reading the same attribute; mirror it for keyboard
    // activation. _pendingFrame stays unset so _onFrameLoad keeps the cursor
    // position instead of snapping back to the top.
    const frame = this._frame()
    if (
      option.dataset.turboFrame &&
      frame &&
      frame.id === option.dataset.turboFrame
    ) {
      if (option.href) frame.src = option.href
      return
    }
    if (option.href) window.location.href = option.href
  }

  // Mouse hover highlights the recent-search pill under the pointer, matching the
  // React RecentSearchesCommandItem group-aria-selected styling. Scoped to recent
  // searches ONLY: actions/results are keyboard- and click-driven, and recent
  // pills sit outside the keyboard cursor's option set (_options), so they carry
  // their own aria-selected toggle instead of going through _moveTo.
  //
  // The highlight is sticky: it stays on the last hovered pill after the pointer
  // leaves, and only moves when another sibling is hovered — exactly one pill is
  // active at a time.
  onPointerHover(event) {
    if (!this.hasRecentSearchesListTarget) return
    const active = event.target.closest('[role="option"]')
    if (!active || !this.recentSearchesListTarget.contains(active)) return
    for (const pill of this.recentSearchesListTarget.querySelectorAll(
      '[role="option"]',
    )) {
      pill.setAttribute("aria-selected", pill === active ? "true" : "false")
    }
  }

  // Hovering an action/result option moves the shared keyboard cursor onto it
  // (cmdk parity: pointer and arrow keys drive ONE selection, so the arrows then
  // continue from the hovered item). These options live in _options(); the
  // aria-selected:bg-foreground/5 styling is already on the links.
  onOptionHover(event) {
    const option = event.target.closest('[role="option"]')
    if (!option) return
    const options = this._options()
    const index = options.indexOf(option)
    if (index >= 0 && index !== this._selectedIndex)
      this._moveTo(index, options)
  }

  // Currently visible, focusable option links, in DOM order.
  // While a query is active the filtered action rows and the result rows are
  // BOTH visible (CONEK-7194) — the cursor spans both listboxes, results first
  // (the frame sits above the actions table), so the first ArrowDown after a
  // fresh search lands on the top result (nothing is auto-selected, CONEK-7048). Idle — or when the actions table is hidden because no page
  // matched — the previous single-listbox behavior holds. Recent pills are
  // never in scope.
  _options() {
    const queryActive =
      this.hasInputTarget &&
      this.inputTarget.value.trim().length >= this.minLengthValue
    let scope = this.itemTargets
    if (this._actionsVisible()) {
      scope = queryActive
        ? [...this.itemTargets, ...this.actionItemTargets]
        : this.actionItemTargets
    }
    return scope.filter((el) => el.offsetParent !== null)
  }

  // ── Actions / results visibility ──────────────────────────────────────────

  _showActions(show) {
    this.actionsTarget.classList.toggle("hidden", !show)
    if (show) {
      // Restoring the actions view always un-filters the rows (CONEK-7194): a
      // filtered query left rows hidden, and only an un-filter brings the
      // full shortcut table back.
      for (const row of this.actionsTarget.querySelectorAll("tr"))
        row.classList.remove("hidden")
      if (this.hasRecentSearchesTarget) this._renderRecentSearches()
    } else if (this.hasRecentSearchesTarget) {
      this.recentSearchesTarget.classList.add("hidden")
    }
    // Point the combobox at whichever listbox is actually visible (APG): the
    // actions listbox while idle, the results frame once a search is showing.
    this._pointAriaControls(
      show ? this.actionsListboxIdValue : this.resultsListboxIdValue,
    )
  }

  // Keeps only the action-item <tr>s whose combined text (group heading + cell
  // labels) matches the query: every whitespace-separated word of the query
  // must be a substring of the row's downcased text. Returns how many rows
  // survived, so the caller can decide whether the container stays visible. A
  // blank query keeps every row.
  _filterActions(query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean)
    let visible = 0
    for (const row of this.actionsTarget.querySelectorAll("tr")) {
      const text = (row.textContent ?? "").toLowerCase()
      const match = words.every((word) => text.includes(word))
      row.classList.toggle("hidden", !match)
      if (match) visible++
    }
    return visible
  }

  _pointAriaControls(id) {
    if (this.hasInputTarget && id)
      this.inputTarget.setAttribute("aria-controls", id)
  }

  _actionsVisible() {
    return !this.actionsTarget.classList.contains("hidden")
  }

  _setLoading(loading) {
    this.loadingTarget.classList.toggle("hidden", !loading)
    this.loadingTarget.classList.toggle("flex", loading)
  }

  _setError(show) {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.toggle("hidden", !show)
      this.errorTarget.classList.toggle("flex", show)
    }
  }

  // H1 / §6.4 parity — a failed quick-search request must not leave the spinner
  // hanging. Turbo never fires turbo:frame-load for a non-2xx frame response, so
  // we catch the failure on the way out: hide the spinner and surface the error
  // row (with a retry). Two gates, both required: the search must be OURS
  // (_pendingFrame — only set by keystroke searches) AND the failed fetch must
  // be OUR frame's. These handlers listen on document, so every turbo fetch on
  // the page lands here — without the target check, an unrelated frame/form
  // failing while a palette search is in flight would surface a bogus error row.
  _onFetchResponse(event) {
    if (!this._pendingFrame) return
    if (event.target !== this._frame()) return
    const response = event.detail?.fetchResponse?.response
    const ok = response && response.status >= 200 && response.status < 300
    if (ok) return
    this._handleFailure()
  }

  _onFetchError(event) {
    if (!this._pendingFrame) return
    if (event.target !== this._frame()) return
    this._handleFailure()
  }

  _handleFailure() {
    this._pendingFrame = false
    this._setLoading(false)
    this._setError(true)
    // retry() re-issues the same URL; if the frame's src still holds it, setting
    // it again to the identical value produces no DOM mutation and Turbo never
    // re-navigates — the error would sit on screen forever. Clear it so retry's
    // assignment is a genuine absent→present change.
    const frame = this._frame()
    if (frame) frame.removeAttribute("src")
  }

  retry() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    if (query.length < this.minLengthValue) {
      this._setError(false)
      return
    }
    this._setError(false)
    this._setLoading(true)
    this._performSearch(query)
  }

  _frame() {
    return this.element.querySelector("turbo-frame")
  }

  _clearFrame() {
    this._pendingFrame = false
    this._setError(false)
    const frame = this._frame()
    if (frame) {
      frame.removeAttribute("src")
      frame.innerHTML = ""
    }
  }

  // ── Recent searches (localStorage, React zustand shape) ───────────────────

  _readRecent() {
    try {
      const raw = localStorage.getItem(this.constructor.STORAGE_KEY)
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      const map = parsed?.state?.recentSearchesMap ?? {}
      // CONEK-2341: drop entries whose key is a UUID (corrupt legacy rows).
      const clean = {}
      for (const [term, entry] of Object.entries(map)) {
        if (!this._isUuid(term)) clean[term] = entry
      }
      return clean
    } catch {
      return {}
    }
  }

  _writeRecent(map) {
    try {
      localStorage.setItem(
        this.constructor.STORAGE_KEY,
        JSON.stringify({ state: { recentSearchesMap: map }, version: 0 }),
      )
    } catch {
      // ignore quota / disabled storage
    }
  }

  _saveRecentSearch(term, href) {
    const map = this._readRecent()
    map[term] = {
      href,
      time: Date.now(),
      keywords: [],
      spaceContext: this._recentScope(),
    }
    let entries = Object.entries(map)
    if (entries.length > this.constructor.MAX_RECENT) {
      entries = entries
        .sort(([, a], [, b]) => b.time - a.time)
        .slice(0, this.constructor.MAX_RECENT)
    }
    this._writeRecent(Object.fromEntries(entries))
  }

  _renderRecentSearches() {
    if (!this.hasRecentSearchesTarget) return
    const map = this._readRecent()
    const scope = this._recentScope()
    const prefix = this._spacePrefix()
    const entries = Object.entries(map)
      .filter(([, entry]) =>
        this._recentMatchesScope(entry.spaceContext, scope),
      )
      .sort(([, a], [, b]) => b.time - a.time)

    if (entries.length === 0) {
      this.recentSearchesTarget.classList.add("hidden")
      return
    }

    this.recentSearchesListTarget.innerHTML = entries
      .map(([term, entry]) => {
        const href = this._rewriteSpacePrefix(entry.href, prefix)
        return `<a href="${this._escape(href)}" role="option" tabindex="-1" data-ui--command-palette-target="item"
          class="group rounded-sm outline-none"
          data-recent-search-name="${this._escape(term)}" data-recent-search-href="${this._escape(href)}">
          <span class="bg-foreground/5 text-foreground-medium group-aria-selected:border-foreground/45 group-aria-selected:bg-foreground/10 group-aria-selected:text-foreground rounded-full border border-transparent px-2.5 py-1 text-xs">${this._escape(term)}</span>
        </a>`
      })
      .join("")
    this.recentSearchesTarget.classList.remove("hidden")
  }

  // ── Recent-search scope ────────────────────────────────────────────────────

  // The bucket recent searches are saved under and filtered by. An explicit
  // recentScopeValue (docs layout passes "docs") wins; otherwise the URL's space
  // prefix (CRM). Keeps platform-wide docs history from mixing with CRM history.
  _recentScope() {
    return this.recentScopeValue || this._spacePrefix()
  }

  // Does a saved entry's spaceContext belong in the current scope? Space scopes
  // (/s:group:branches) use branch-subset containment as before; any other scope
  // (docs, or empty) is exact-match, so docs and CRM histories never cross.
  _recentMatchesScope(saved, scope) {
    if (scope.startsWith("/s:"))
      return this._spaceContextContained(saved, scope)
    return (saved ?? "") === scope
  }

  // ── Space-prefix helpers (mirror React url-builder.ts) ─────────────────────

  _spacePrefix() {
    const m = /^\/s:[^:]+:[^/]+/.exec(window.location.pathname)
    return m ? m[0] : ""
  }

  _rewriteSpacePrefix(href, currentPrefix) {
    if (!currentPrefix) return href
    return href.replace(/^\/s:[^:]+:[^/]+/, currentPrefix)
  }

  _spaceContextContained(saved, current) {
    const parse = (u) => {
      const m = /\/s:([^:]+):([^/]+)/.exec(u || "")
      return m ? { group: m[1], branches: m[2].split(",") } : null
    }
    const s = parse(saved)
    const c = parse(current)
    if (!s || !c || s.group !== c.group) return false
    const currentBranches = new Set(c.branches)
    return s.branches.every((b) => currentBranches.has(b))
  }

  // ── Misc ───────────────────────────────────────────────────────────────

  _onGlobalKeydown(event) {
    if (
      event.key.toLowerCase() === this.triggerKeyValue.toLowerCase() &&
      (event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault()
      this.open()
    }
  }

  // §5.1 contract: opens the dialog in response to a `command-palette:open`
  // window event (React trigger on Inertia pages, drawings-editor toolbar).
  _onGlobalOpen() {
    this.open()
  }

  _isMac() {
    return navigator.platform?.toLowerCase().includes("mac") ?? false
  }

  _isUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML.replace(/"/g, "&quot;")
  }
}
